import { memo } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { Link } from "~/common/components/Link";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { EMAIL_PATTERN } from "~/utils/constants";
import { Controller } from "react-hook-form";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { Trans, useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import { TERMS_OF_USE_PAGE } from "~/utils/url/staticUrls";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Box, Button, Card, Stack, TextField, styled } from "~/common/mui";
import { CustomerRegistrationBanner } from "~/features/registration/components/CustomerRegistrationBanner/CustomerRegistrationBanner";
import {
  ErrorMessage,
  HelperLabelErrorMessage,
  RegisterContactSalesSection,
  useRegisterForFree,
} from "~/features/registration/hooks/useRegisterForFree";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const RegisterForFreeMobile = ({ showCustomerRegistration }: { showCustomerRegistration?: boolean }) => {
  const { t } = useTranslation();
  const {
    errors,
    watch,
    control,
    resetPopupOpen,
    loginPopupOpen,
    openLogin,
    closeLogin,
    closeReset,
    onResetClick,
    onSubmitHandler,
  } = useRegisterForFree();

  return (
    <RegisterFreeMobileRoot className="scha__register-free register-mobile">
      <Box sx={{ display: "flex", flexFlow: "column", gap: "32px" }}>
        {showCustomerRegistration && (
          <>
            <PageTitle title={t("page.registerFree.title")} />
            <CustomerRegistrationBanner />
          </>
        )}
        <div className="scha__register-free__title">
          <PageTitle
            title={
              <Trans i18nKey={"page.registerFree.mobile.title"}>
                Register for free for
                <strong>medias</strong>
                <RegisterFreeMediasPlus className={"scha__register-free__medias-plus"}>Plus</RegisterFreeMediasPlus>
              </Trans>
            }
          />
          <h3 className="scha__register-free__title__subtitle">
            <Trans i18nKey={"page.registerFree.mobile.subtitle"}>
              With
              <strong>medias</strong>
              <RegisterFreeMediasPlus className={"scha__register-free__medias-plus"}>Plus</RegisterFreeMediasPlus>
              you get extended access to features and tools.
            </Trans>
          </h3>
        </div>
        <div className="scha__register-free form">
          <div className="scha__register-free__main-content">
            <form onSubmit={onSubmitHandler}>
              <Card
                component={Stack}
                gap={3}
                sx={{
                  borderRadius: 0,
                  padding: 2,
                }}
              >
                <Controller
                  name={"email"}
                  control={control}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={t("page.registerFree.label")}
                      inputRef={ref}
                      error={!!error}
                      helperText={<HelperLabelErrorMessage error={error} />}
                      inputProps={{ "data-test-id": "register-for-free-email" }}
                      {...inputProps}
                    />
                  )}
                  rules={{
                    required: { value: true, message: "error.invalidEmail" },
                    pattern: { value: EMAIL_PATTERN, message: "error.invalidEmail" },
                  }}
                />

                <Stack gap={2}>
                  <Controller
                    name={"generalTerms"}
                    control={control}
                    rules={{ required: { value: true, message: "error.tos" } }}
                    render={({ field: { value, onChange } }) => (
                      <CheckboxWithLabel
                        data-test-id={"register-for-free__general-terms"}
                        checked={value}
                        onCheck={onChange}
                      >
                        {t("page.registerFree.terms-part1")}{" "}
                        <a
                          className="scha__register-free__main-content__right__checkbox-area__text__link"
                          href={TERMS_OF_USE_PAGE}
                        >
                          {t("page.registerFree.terms-link")}
                        </a>{" "}
                        {t("page.registerFree.terms-part2")}
                      </CheckboxWithLabel>
                    )}
                  />
                  <ErrorMessage error={errors.generalTerms} />

                  <Controller
                    name={"privacyPolicy"}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MarketingConsentCheckbox
                        data-test-id={"register-for-free__privacy-policy"}
                        checked={value}
                        onCheck={onChange}
                      />
                    )}
                  />

                  <div className="scha__register-free__main-content__right__mandatory">
                    {t("page.registerFree.mandatory")}
                  </div>
                </Stack>
                <Button
                  // className={"mt-4"}
                  startIcon={<PersonIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
                  fullWidth={true}
                  data-test-id={"register-for-free__submit"}
                  onClick={onSubmitHandler}
                >
                  {t("page.registerFree.register")}
                </Button>

                <div className="scha__register-free__already">
                  <span>{t("page.registerFree.already")} </span>
                  <Link onClick={openLogin} className="scha__register-free__already__link">
                    {t("page.registerFree.already-link")}
                  </Link>
                </div>

                <RegisterContactSalesSection />
              </Card>
            </form>
            {loginPopupOpen && <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />}
            {resetPopupOpen && <ResetPasswordPopup initialEmail={watch("email")} close={closeReset} />}
          </div>
        </div>
      </Box>

      <RegistrationMediasTable />
    </RegisterFreeMobileRoot>
  );
};

const RegistrationMediasTable = () => {
  const { t } = useTranslation();

  const options = [
    {
      label: t("page.registerForFree.baner.all"),
      medias: t("page.registerForFree.baner.limited"),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.lc"),
      medias: t("page.registerForFree.baner.limited"),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.cad"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.saveLinear"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.compareProducts"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.trainingCampus"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.keep.new"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.send.new"),
      medias: (
        <div className="circle disabled">
          <CloseIcon />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <CheckIcon />
        </div>
      ),
    },
  ];

  return (
    <RegistrationOptions className="options">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align={"center"}>
              <MediasLogo variant={"general"} />
            </TableCell>
            <TableCell align={"center"}>
              <MediasLogo variant={"plus"} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {options.map((option, i) => (
            <TableRow key={i}>
              <TableCell className={"label"}>{option.label}</TableCell>
              <TableCell className={"medias"}>{option.medias}</TableCell>
              <TableCell className={"mediasPlus"}>{option.mediasPlus}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </RegistrationOptions>
  );
};

export default memo(RegisterForFreeMobile);

const RegisterFreeMediasPlus = styled("span")(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const RegisterFreeMobileRoot = styled("div")(({ theme }) => ({
  "& > h1": {
    ...theme.typography.titleLarge,
  },
  "& > div > h3, & .scha__register-free__title__subtitle": {
    ...theme.typography.titleMedium,
    marginBottom: 0,
  },
  "& > .scha__spacer": {
    marginTop: `${theme.spacing(1)} !important`,
    marginBottom: `${theme.spacing(2)} !important`,
  },
  "& .scha__register-free__main-content": {
    marginBottom: theme.spacing(4),
  },
  "& .scha__card": {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& > *": {
      width: "100%",
    },
    "& .scha__register-free__already": {
      textAlign: "center",
      padding: theme.spacing(1),
      marginTop: theme.spacing(1),
      marginBottom: 0,
    },
    "& > button": {
      marginTop: theme.spacing(3),
    },
  },
}));

const RegistrationOptions = styled("div")(({ theme }) => ({
  "& table thead th": {
    whiteSpace: "nowrap",
    "& span > span": {
      color: theme.palette.secondary.main,
    },
    "& img": {
      maxWidth: "initial",
      maxHeight: "initial",
    },
  },
  "& table tbody tr": {
    background: "none",
    "& td": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRadius: 0,
      padding: theme.spacing(1.5),
      "&.label": {
        ...theme.typography.labelMedium,
        fontWeight: "bold",
        whiteSpace: "normal",
      },
      "&.medias, &.mediasPlus": {
        verticalAlign: "middle",
        textAlign: "center",
      },
      "& i": {
        color: theme.palette.common.white,
      },
    },
  },
  "& .circle": {
    display: "inline-block",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    position: "relative",
    marginBottom: theme.spacing(-0.75),
    "&.disabled": {
      backgroundColor: theme.palette.action.disabled,
    },
    "& i": {
      position: "absolute",
      transform: "translate(-50%, -50%)",
      top: "50%",
      left: "50%",
    },
  },
}));
